import { useMutation, useQuery } from "react-query";

import type { StartSurveyResponse, UserSurvey } from "@jti/rest-api-types";

import { MUTATION_KEYS, QUERY_KEYS } from "../../../constants/queryKeys";
import { SurveyApi } from "../../../services";

export const useAvailableSurveysStatus = (userUuid?: string, ciamToken?: string) => {
  const fetchSurveysStatus = async () => {
    if (!userUuid) {
      throw new Error("User uuid is missing");
    }

    return await SurveyApi.init().getAvailableSurveys(userUuid);
  };

  const { data, isLoading, isError, error, refetch } = useQuery<UserSurvey[] | undefined, Error>(
    [QUERY_KEYS.surveysStatus, userUuid, ciamToken],
    fetchSurveysStatus,
    {
      enabled: !!ciamToken && !!userUuid,
      retry: 3,
      retryDelay: 500,
      refetchOnWindowFocus: false,
    }
  );

  return {
    refetch,
    surveysStatus: data,
    surveyStatusError: error,
    isSurveysStatusError: isError,
    isSurveysStatusLoading: isLoading,
  };
};

type StartSurveyVariables = {
  userUuid: string;
  ciamToken: string;
  survey_id: string;
  userInputCode: string;
};

export const useStartSurvey = () => {
  const fetchStartSurvey = async ({ userUuid, survey_id, userInputCode }: StartSurveyVariables) => {
    return await SurveyApi.init().startSurvey(userUuid, survey_id, userInputCode);
  };

  const { isError, isLoading, mutateAsync, error, reset } = useMutation<
    StartSurveyResponse,
    Error & { error_code?: string },
    StartSurveyVariables
  >(MUTATION_KEYS.startSurvey, fetchStartSurvey);

  return {
    resetStartSurvey: reset,
    startSurvey: mutateAsync,
    startSurveyError: error,
    isStartSurveyError: isError,
    isStartSurveyLoading: isLoading,
  };
};

export const useValidateSurveySerialCode = () => {
  const { isError, isLoading, mutateAsync, error, reset } = useMutation<
    void,
    Error & { error_code?: string },
    StartSurveyVariables
  >(MUTATION_KEYS.validateSurveySerialCode, async ({ userUuid, survey_id, userInputCode }: StartSurveyVariables) => {
    await SurveyApi.init().validateSurveySerialCode(userUuid, survey_id, userInputCode);
  });

  return {
    resetValidateSurveySerialCode: reset,
    validateSurveySerialCode: mutateAsync,
    validateSurveySerialCodeError: error,
    isValidateSurveySerialCodeError: isError,
    isValidateSurveySerialCodeLoading: isLoading,
  };
};
